import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Form, Modal } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { dayLabel, formatDay, formatDateTime } from '../utils/formatDate';
import { GASES, GAS_LABEL, gasLabel, isCgcClient } from '../utils/gases';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Chart.js draws to a <canvas>, which doesn't resolve CSS custom properties — literal values of the --mm- tokens.
const CYAN = '#12B8B2';
const SLATE = 'rgba(157,179,194,0.55)';
const MUTED = '#9DB3C2';
const LINE = 'rgba(234,242,245,0.10)';

const CHART_OPTIONS = {
  maintainAspectRatio: false,
  plugins: { legend: { labels: { color: MUTED, boxWidth: 12, boxHeight: 12 } } },
  scales: {
    x: { ticks: { color: MUTED }, grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0, color: MUTED }, grid: { color: LINE } },
  },
};

const GAS_COLUMNS = [...GASES, 'sin_dato'];
const DAY = 86400000;

// Where Caribbean Gas' own cylinders are (GET /api/cgc-tanks, backend utils/cgcTanks.js): per client and gas, CGC
// cylinders sent on despachos minus what came back (empties brought back, devoluciones) ± adjustments — counted from
// the day following began, so it's "net since then", not everything a client holds. Admins correct it with "Ajustar"
// (a count at the client, or a write-off). Sentinel looks for patterns in the same numbers.
const CgcTanksPanel = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ client: '', gas: '', quantity: '', reason: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // "Conteo inicial": what each client holds of each gas, typed in one form (key client|gas → the counted number)
  const [countOpen, setCountOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const [countReason, setCountReason] = useState('Conteo inicial');
  const [countSearch, setCountSearch] = useState('');
  const [countError, setCountError] = useState('');
  const isAdmin = localStorage.getItem('userType') === 'admin';

  const load = useCallback(() => {
    fetch('/api/cgc-tanks').then((res) => res.json()).then(setData)
      .catch((err) => console.error('❌ Error fetching CGC cylinders:', err));
  }, []);
  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    const weeks = data?.weeks || [];
    return {
      labels: weeks.map((w) => `${dayLabel(w.from)}${w.partial ? ' (en curso)' : ''}`),
      datasets: [
        { label: 'Enviados a clientes', data: weeks.map((w) => w.out), backgroundColor: CYAN, borderRadius: 4 },
        { label: 'Vacíos devueltos', data: weeks.map((w) => w.back), backgroundColor: SLATE, borderRadius: 4 },
      ],
    };
  }, [data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.clients || [])
      .filter((c) => !q || c.client.toLowerCase().includes(q))
      .map((c) => ({
        ...c,
        daysWithoutEmpties: c.net > 0
          ? Math.floor((Date.now() - new Date(c.lastBackAt || c.firstSentAt || data.since).getTime()) / DAY)
          : null,
      }));
  }, [data, search]);

  const loadClients = () => {
    fetch('/api/clients').then((res) => res.json())
      // GET /api/clients answers plain names
      .then((list) => setClients((Array.isArray(list) ? list : []).map((c) => (typeof c === 'string' ? c : c.name)).filter((n) => n && !isCgcClient(n)).sort((a, b) => a.localeCompare(b))))
      .catch(() => setClients([]));
  };

  const openAdjust = () => {
    setError('');
    setForm({ client: '', gas: '', quantity: '', reason: '' });
    loadClients();
    setDialog(true);
  };

  const heldNow = (client, gas) => data?.clients?.find((c) => c.client === client)?.byGas?.[gas]?.net || 0;

  const openCount = () => {
    setCountError('');
    setCounts({});
    setCountSearch('');
    setCountReason('Conteo inicial');
    loadClients();
    setCountOpen(true);
  };

  const countLines = Object.entries(counts)
    .filter(([, v]) => v !== '')
    .map(([key, v]) => {
      const [client, gas] = key.split('|');
      return { client, gas, quantity: Number(v) };
    });

  const saveCount = async () => {
    if (!countLines.length) {
      setCountError('Escribe al menos un número.');
      return;
    }
    if (countLines.some((l) => !Number.isInteger(l.quantity) || l.quantity < 0)) {
      setCountError('Cada número debe ser un entero de cero o más.');
      return;
    }
    setSaving(true);
    setCountError('');
    try {
      const res = await fetch('/api/cgc-tanks/count', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counts: countLines, reason: countReason }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'No se pudo guardar el conteo.');
      setCountOpen(false);
      load();
    } catch (err) {
      setCountError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAdjust = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/cgc-tanks/adjust', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: form.client, gas: form.gas || undefined, quantity: Number(form.quantity), reason: form.reason }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'No se pudo guardar el ajuste.');
      setDialog(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const totals = data.totals || { net: 0, byGas: {} };

  return (
    <div className="row mb-4">
      <div className="col-12">
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: '8px' }}>
              <div>
                <Card.Title className="mb-0">Cilindros CGC en Clientes</Card.Title>
                <small className="text-muted">Neto desde el {formatDay(data.since)}: enviados menos devueltos, por cliente y gas</small>
              </div>
              {isAdmin && (
                <div className="d-flex" style={{ gap: '8px' }}>
                  <Button size="sm" variant="info" onClick={openCount}>Conteo inicial</Button>
                  <Button size="sm" variant="outline-light" onClick={openAdjust}>Ajustar</Button>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>En clientes (neto)</div>
                <div className="text-mm-cyan" style={{ fontSize: '2.6rem', fontWeight: 700, lineHeight: 1.1 }}>{totals.net}</div>
                <div className="mt-2" style={{ fontSize: '0.9rem' }}>
                  {GAS_COLUMNS.filter((g) => totals.byGas?.[g]).map((g) => (
                    <div key={g}>{gasLabel(g)}: <strong>{totals.byGas[g].net}</strong></div>
                  ))}
                  <div className="text-muted mt-1">Enviados {totals.sent} · vacíos devueltos {totals.back} · devoluciones {totals.returned}</div>
                </div>
              </div>
              <div className="col-md-8 mb-3" style={{ height: 220 }}>
                <Bar data={chartData} options={CHART_OPTIONS} />
              </div>
            </div>

            <Form.Control
              size="sm"
              className="mb-2"
              placeholder="Buscar cliente…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
            <Table size="sm" striped className="mb-1">
              <thead>
                <tr>
                  <th>Cliente</th>
                  {GAS_COLUMNS.map((g) => <th key={g} className="text-right">{gasLabel(g)}</th>)}
                  <th className="text-right">Enviados</th>
                  <th className="text-right">Devueltos</th>
                  <th className="text-right">Ajustes</th>
                  <th className="text-right">Neto</th>
                  <th className="text-right">Sin vacíos</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={GAS_COLUMNS.length + 6} className="text-muted">Todavía no hay cilindros de CGC enviados a clientes.</td></tr>
                )}
                {rows.map((c) => (
                  <tr key={c.client}>
                    <td>{c.client}</td>
                    {GAS_COLUMNS.map((g) => <td key={g} className="text-right">{c.byGas?.[g]?.net || '—'}</td>)}
                    <td className="text-right">{c.sent}</td>
                    <td className="text-right">{c.back + c.returned}</td>
                    <td className="text-right">{c.adjusted || '—'}</td>
                    <td className={`text-right ${c.net > 0 ? 'text-mm-yellow' : ''}`}>{c.net}</td>
                    <td className={`text-right ${c.daysWithoutEmpties >= 45 ? 'text-mm-red' : ''}`}>
                      {c.daysWithoutEmpties == null ? '—' : `${c.daysWithoutEmpties} d`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <small className="text-muted">
              Cuenta desde que se empezó a registrar quién recibe y quién devuelve los cilindros de CGC: un cliente que
              devuelve cilindros de antes puede quedar en negativo. "Sin vacíos" = días desde que devolvió vacíos por última
              vez mientras tiene cilindros. Para corregir con un conteo en el cliente o dar de baja cilindros perdidos, usa
              "Ajustar".
            </small>

            {(data.adjustments || []).length > 0 && (
              <>
                <h6 className="mt-3">Ajustes recientes</h6>
                <Table size="sm" striped className="mb-0">
                  <tbody>
                    {data.adjustments.slice(0, 8).map((a) => (
                      <tr key={a._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.at)}</td>
                        <td>{a.client}</td>
                        <td>{gasLabel(a.gas)}</td>
                        <td>{a.kind === 'count' ? 'Conteo' : 'Ajuste'}</td>
                        <td className={`text-right ${a.quantity > 0 ? 'text-mm-green' : 'text-mm-red'}`}>{a.quantity > 0 ? `+${a.quantity}` : a.quantity}</td>
                        <td>{a.reason}</td>
                        <td>{a.by}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={countOpen} onHide={() => setCountOpen(false)} size="lg" scrollable centered>
        <Modal.Header closeButton>
          <Modal.Title>Conteo inicial de cilindros de CGC</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Escribe cuántos cilindros de CGC tiene hoy cada cliente, por gas. Cada número queda como lo que tiene ese
            cliente ahora, y desde aquí sube y baja con cada despacho y cada vacío que devuelva. Deja en blanco lo que no
            contaste (no cambia). No toca En Planta ni Por Llenar.
          </p>
          <Form.Control
            size="sm"
            className="mb-2"
            placeholder="Buscar cliente…"
            value={countSearch}
            onChange={(e) => setCountSearch(e.target.value)}
          />
          <Table size="sm" striped className="mb-2">
            <thead>
              <tr>
                <th>Cliente</th>
                {GASES.map((g) => <th key={g} className="text-right">{GAS_LABEL[g]}</th>)}
              </tr>
            </thead>
            <tbody>
              {clients
                .filter((name) => !countSearch.trim() || name.toLowerCase().includes(countSearch.trim().toLowerCase()))
                .map((name) => (
                  <tr key={name}>
                    <td>{name}</td>
                    {GASES.map((g) => {
                      const key = `${name}|${g}`;
                      return (
                        <td key={g} className="text-right">
                          <Form.Control
                            size="sm"
                            type="number"
                            min="0"
                            step="1"
                            style={{ maxWidth: 90, marginLeft: 'auto', textAlign: 'right' }}
                            placeholder={String(heldNow(name, g))}
                            value={counts[key] ?? ''}
                            onChange={(e) => setCounts((c) => ({ ...c, [key]: e.target.value }))}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </Table>
          <Form.Group>
            <Form.Label>Razón</Form.Label>
            <Form.Control value={countReason} onChange={(e) => setCountReason(e.target.value)} />
          </Form.Group>
          {countError && <div className="text-mm-red mt-2">{countError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <span className="text-muted mr-auto" style={{ fontSize: '0.85rem' }}>
            {countLines.length} números · {countLines.reduce((s, l) => s + (Number(l.quantity) || 0), 0)} cilindros
          </span>
          <Button variant="secondary" onClick={() => setCountOpen(false)}>Cancelar</Button>
          <Button variant="info" onClick={saveCount} disabled={saving}>{saving ? 'Guardando…' : 'Guardar conteo'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog} onHide={() => setDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Ajustar cilindros de CGC en un cliente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Cliente</Form.Label>
            <Form.Control as="select" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}>
              <option value="">Elige el cliente…</option>
              {clients.map((name) => <option key={name} value={name}>{name}</option>)}
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label>Gas</Form.Label>
            <Form.Control as="select" value={form.gas} onChange={(e) => setForm({ ...form, gas: e.target.value })}>
              <option value="">Sin especificar</option>
              {GASES.map((g) => <option key={g} value={g}>{GAS_LABEL[g]}</option>)}
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label>Cambio</Form.Label>
            <Form.Control type="number" step="1" value={form.quantity} placeholder="-2 = ya no están (perdidos, dados de baja)" onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            <Form.Text className="text-muted">Positivo si el cliente tiene más de los que dice la tabla; negativo para dar de baja.</Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>Razón</Form.Label>
            <Form.Control value={form.reason} placeholder="Conteo en el cliente, cilindro perdido…" onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Form.Group>
          {error && <div className="text-mm-red mt-2">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="info" onClick={saveAdjust} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CgcTanksPanel;
